import { useCallback, useEffect, useMemo, useRef } from "react";
import { Navigate, useLocation } from "react-router";
import type { FlowStep } from "~/types";
import { FLOW_BASE_PATH, FLOW_FINISHED_FLAG } from "~/constants/flows";

/**
 * Hook to check if the flow is in progress
 */
interface UseFlowGuardReturn {
    redirectElement: React.ReactElement | null;
    resetFlow: () => void;
}

export const useFlowGuard = ({
    canResetFlow = false,
    flowBasePath,
    resetFlow,
    steps,
    currentStepIndex,
    basePath,
    isFinished = false,
}: {
    canResetFlow?: boolean;
    flowBasePath?: string;
    resetFlow?: (withRedirect?: boolean) => void;
    steps?: FlowStep[];
    basePath?: string;
    currentStepIndex?: number;
    isFinished?: boolean;
}): UseFlowGuardReturn => {
    const location = useLocation();
    const resetFlowRef = useRef(resetFlow);
    const initializedRef = useRef(false);

    // Update ref when resetFlow changes to avoid stale closures
    useEffect(() => {
        resetFlowRef.current = resetFlow;
    }, [resetFlow]);

    // Check if we're currently in a flow
    const isInFlow = useMemo(() => {
        if (!flowBasePath || !location.pathname) return false;
        return location.pathname.includes(flowBasePath);
    }, [flowBasePath, location.pathname]);

    // Update the finished flag in sessionStorage when isFinished changes
    useEffect(() => {
        if (typeof window === "undefined" || canResetFlow) return;

        // Only set the flag when flow is finished
        if (isInFlow && isFinished) {
            sessionStorage.setItem(FLOW_FINISHED_FLAG, "true");
        }

        // Don't remove the flag here - let the cleanup effect handle it
        // This prevents race conditions when navigating away
    }, [isFinished, isInFlow, canResetFlow]);

    // Memoize the reset flow callback to prevent unnecessary re-renders
    const resetFlowCallback = useCallback(() => {
        if (typeof window !== "undefined") {
            sessionStorage.removeItem(FLOW_BASE_PATH);
            sessionStorage.removeItem(FLOW_FINISHED_FLAG);
        }
        resetFlowRef.current?.();
        initializedRef.current = false;
    }, []);

    // When leaving a flow, clean it up
    useEffect(() => {
        if (typeof window === "undefined") return;

        const storedFlowPath = sessionStorage.getItem(FLOW_BASE_PATH);
        const isFlowFinished =
            sessionStorage.getItem(FLOW_FINISHED_FLAG) === "true";

        // If there's an active flow stored and we're not in it anymore, clean up
        if (storedFlowPath && !location.pathname.includes(storedFlowPath)) {
            sessionStorage.removeItem(FLOW_BASE_PATH);
            sessionStorage.removeItem(FLOW_FINISHED_FLAG);

            // If canResetFlow is false (individual flow component), trigger reset
            // BUT only if the flow wasn't finished (if finished, user is intentionally leaving)
            if (!canResetFlow && resetFlowRef.current && !isFlowFinished) {
                resetFlowRef.current(false); // Pass false to avoid navigation during cleanup
            }
        }
    }, [canResetFlow, location.pathname]);

    // Handle flow entry and reset logic - only run once when entering the flow
    useEffect(() => {
        if (
            !canResetFlow &&
            isInFlow &&
            flowBasePath &&
            typeof window !== "undefined" &&
            !initializedRef.current
        ) {
            const storedFlowPath = sessionStorage.getItem(FLOW_BASE_PATH);

            // If sessionStorage is empty, we're returning after leaving - reset first
            if (!storedFlowPath && resetFlowRef.current) {
                resetFlowRef.current();
            }

            // Mark this flow as active
            sessionStorage.setItem(FLOW_BASE_PATH, flowBasePath);
            initializedRef.current = true;
        }

        // Reset the initialized flag when leaving the flow
        if (!isInFlow && initializedRef.current) {
            initializedRef.current = false;
        }
    }, [canResetFlow, isInFlow, flowBasePath]);

    // Memoize first path calculation
    const firstPath = useMemo(() => {
        if (!steps?.length || !basePath) return undefined;
        return `${basePath}/${steps[0].id}/`;
    }, [basePath, steps?.length, steps?.[0]?.id]); // More specific dependencies

    // Memoize redirect logic
    const mustRedirect = useMemo(() => {
        // Don't redirect if flow is finished
        if (isFinished) {
            return false;
        }

        if (
            !steps?.length ||
            !firstPath ||
            currentStepIndex === undefined ||
            currentStepIndex < 0
        ) {
            return false;
        }

        const hasPrev = currentStepIndex > 0 && !!steps[currentStepIndex - 1];
        const prevIncomplete =
            hasPrev && !steps[currentStepIndex - 1].isCompleted;
        return prevIncomplete && location.pathname !== firstPath;
    }, [
        isFinished,
        steps?.length,
        firstPath,
        currentStepIndex,
        location.pathname,
        // Include isCompleted of the previous step specifically
        currentStepIndex !== undefined && currentStepIndex > 0
            ? steps?.[currentStepIndex - 1]?.isCompleted
            : null,
    ]);

    // Memoize the redirect element to prevent unnecessary recreations
    const redirectElement = useMemo(() => {
        if (!mustRedirect || !firstPath) return null;

        return (
            <Navigate
                to={firstPath}
                replace
                state={{ __resetAfterNav: true }}
            />
        );
    }, [mustRedirect, firstPath]);

    return {
        redirectElement,
        resetFlow: resetFlowCallback,
    };
};
